import React from "react";
import { ResetIcon } from "@components/Common/SvgIcons/ResetIcon";

import { useTranslation } from "react-i18next";
import {
    Card,
    SectionHeader,
    SectionTitle,
    ToggleContainer,
    ToggleLabel,
    SmallToggle,
    LanguageTabs,
    LanguageTab,
    InputRow,
    InputLabel,
    Input,
    InputWrapper,
    ResetButton,
} from "./styled";
import { ICON_SIZE_SMALL } from "../shared/constants";

type SectionTitleCardProps = {
    mainColor: string;
    languages: Array<{ code: string; label: string }>;
    selectedLanguage: string;
    onLanguageChange: (lang: string) => void;
    defaultSectionTitles: Record<string, string>;
    sectionTitles: Record<string, string>;
    onSectionTitleChange: (lang: string, value: string) => void;
    sectionTitleVisible: boolean;
    onTitleVisibleToggle: () => void;
    onResetSectionTitle: () => void;
    onResetAllSectionTitles: () => void;
};

export function SectionTitleCard({
    mainColor,
    languages,
    selectedLanguage,
    onLanguageChange,
    defaultSectionTitles,
    sectionTitles,
    onSectionTitleChange,
    sectionTitleVisible,
    onTitleVisibleToggle,
    onResetSectionTitle,
    onResetAllSectionTitles,
}: SectionTitleCardProps): JSX.Element {
    const { t } = useTranslation();

    return (
        <Card data-testid="scan-to-ads-section-title-card">
            <SectionHeader>
                <SectionTitle>{t("Section title")}</SectionTitle>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <ResetButton
                        data-testid="scan-to-ads-reset-all-titles"
                        mainColor={mainColor}
                        onClick={onResetAllSectionTitles}
                        title={t("Reset all")}
                        aria-label={t("Reset all")}
                    >
                        <ResetIcon style={{ width: ICON_SIZE_SMALL, height: ICON_SIZE_SMALL }} />
                    </ResetButton>
                    <ToggleContainer>
                        <ToggleLabel>{t("Visible")}</ToggleLabel>
                        <SmallToggle
                            data-testid="scan-to-ads-toggle-title-visible"
                            isActive={sectionTitleVisible}
                            mainColor={mainColor}
                            onClick={onTitleVisibleToggle}
                            aria-label={t("Toggle section title visibility")}
                        />
                    </ToggleContainer>
                </div>
            </SectionHeader>

            <LanguageTabs data-testid="scan-to-ads-language-tabs">
                {languages.map((lang) => (
                    <LanguageTab
                        key={lang.code}
                        data-testid={`scan-to-ads-lang-${lang.code}`}
                        isActive={selectedLanguage === lang.code}
                        mainColor={mainColor}
                        onClick={() => onLanguageChange(lang.code)}
                    >
                        {lang.label}
                    </LanguageTab>
                ))}
            </LanguageTabs>

            <InputRow>
                <InputLabel>{t("Title")}</InputLabel>
                <Input
                    data-testid="scan-to-ads-default-title-input"
                    value={defaultSectionTitles[selectedLanguage] || ""}
                    readOnly
                    style={{ backgroundColor: "#f5f5f5", color: "#999" }}
                />
                <InputWrapper>
                    <Input
                        data-testid="scan-to-ads-custom-title-input"
                        value={sectionTitles[selectedLanguage] || ""}
                        onChange={(e) =>
                            onSectionTitleChange(
                                selectedLanguage,
                                e.target.value
                            )
                        }
                        placeholder={t("Customized")}
                    />
                    <ResetButton
                        data-testid="scan-to-ads-reset-title"
                        mainColor={mainColor}
                        onClick={onResetSectionTitle}
                        title={t("Reset")}
                        aria-label={t("Reset")}
                    >
                        <ResetIcon style={{ width: ICON_SIZE_SMALL, height: ICON_SIZE_SMALL }} />
                    </ResetButton>
                </InputWrapper>
            </InputRow>
        </Card>
    );
}
